import baseFormValidators from "@/composables/validations/baseFormValidators.js";
import useFileInputValidations from "@/composables/validations/useFileInputValidations.js";
import walletAddressValidator from "@/composables/validations/walletAddressValidator.js";
import { useOccupationStore } from "@/stores/forms/occupationFormStore";

const { isEmpty, minLength, isEmail, isValidMobile } = baseFormValidators();
const {
  isFileTypeAllowed,
  isSingleFileSizeInLimits,
  areMultiFileSizesInLimits,
  areMultiFileTypesAllowed,
} = useFileInputValidations();
const { validateWalletByBlockchains, validateWalletBySelectedBlockchain } =
  walletAddressValidator();

export default function useFormValidation() {
  const validateNameField = (fieldName, fieldValue) => {
    if (!fieldValue) {
      return;
    }

    return minLength(fieldValue, 1);
  };

  const validateEmailField = (fieldName, fieldValue) => {
    if (!fieldValue) {
      return;
    }

    return isEmpty(fieldName, fieldValue) || isEmail(fieldName, fieldValue);
  };

  const validateMobileField = (mobile, countryCode) => {
    if (!mobile) {
      return;
    }

    return isValidMobile(mobile, countryCode);
  };

  const validateOccupationField = (fieldName, fieldValue) => {
    const occupationStore = useOccupationStore();
    return occupationStore.getErrorByField("occupationOther");
  };

  const validateCheckboxField = (fieldName, fieldValue) => {
    if (!fieldValue || fieldValue == false) {
      return "";
    }
    return isEmpty(fieldName, fieldValue);
  };

  const validateCountryField = (fieldName, country, countryList) => {
    return !countryList.includes(country)
      ? `The input is not a valid ${fieldName}`
      : "";
  };

  const validateVerificationCodeField = (field, pincodeValue) => {
    if (!pincodeValue) {
      return;
    }
    const pincodeLength = import.meta.env.VITE_APP_PINCODE_LENGTH;

    return pincodeValue.length != pincodeLength
      ? `The verification code is invalid`
      : "";
  };

  const validateWalletAddressField = async (walletAddress, blockchain) => {
    if (!walletAddress || !blockchain || blockchain.length === 0) {
      return "";
    }

    if (blockchain.includes("No valid blockchains available")) {
      return `The wallet address is invalid`;
    }

    const validatedWalletAddress = await validateWalletBySelectedBlockchain(
      walletAddress,
      blockchain
    );

    return Object.values(validatedWalletAddress).includes(false)
      ? `The wallet address is invalid`
      : "";
  };
  const validateFiatField = (fiatAmount, fiatType, transactionalLimit) => {
    if (!fiatAmount || !transactionalLimit) {
      return "";
    }
    const { min, max } = transactionalLimit;

    return parseInt(fiatAmount, 10) > parseInt(max, 10) ||
      parseInt(fiatAmount, 10) < parseInt(min, 10)
      ? `${fiatType} orders must be between ${min} and ${max}`
      : "";
  };

  const validateCoinField = (coinAmount, coinType, blockchain, coinData) => {
    if (!coinAmount || !coinData || !coinType) {
      return "";
    }

    const activeCoinData = coinData.find(
      (coin) => coinType === coin?.coin_code
    );

    const minValueByCoin = activeCoinData?.min_value;
    const blockchainDataByCoin = activeCoinData.blockchains.find(
      (coin) => blockchain.value === coin.code
    );
    const minValueByBlockchainCoin = blockchainDataByCoin?.min_value;

    return coinAmount < minValueByBlockchainCoin || coinAmount < minValueByCoin
      ? `Please enter an amount greater or equal to ${
          minValueByBlockchainCoin || minValueByCoin
        }.`
      : "";
  };

  const validateBlockchainField = async (walletAddress, blockchains) => {
    if (!walletAddress || !blockchains || blockchains.length === 0) {
      return "";
    }

    const validatedWalletAddress = await validateWalletByBlockchains(
      walletAddress,
      blockchains
    );
    const walletsAreFalse = (wallet) => wallet === false;

    return Object.values(validatedWalletAddress).every(walletsAreFalse)
      ? "No blockchains found by wallet"
      : "";
  };

  const validateFileInput = (fieldName, file) => {
    return isFileTypeAllowed(file) || isSingleFileSizeInLimits(file);
  };

  const validateMultiFileInput = (fieldName, files) => {
    return areMultiFileSizesInLimits(files) || areMultiFileTypesAllowed(files);
  };

  const validateDobField = (fieldName, dob) => {
    // https://stackoverflow.com/a/7091965
    function getAge(dateString) {
      const today = new Date();
      const birthDate = new Date(dateString);
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    }
    const splitDob = dob.split("-");
    const dobDay = splitDob[0];
    const dobMonth = splitDob[1];
    const dobYear = splitDob[2];

    const formatToDateString = `${dobYear}-${dobMonth}-${dobDay}`;

    const age = getAge(formatToDateString);

    const allowedUpper = import.meta.env.VITE_APP_HIGHEST_ALLOWED_DOB;
    const allowedLower = import.meta.env.VITE_APP_LOWEST_ALLOWED_DOB;

    if (splitDob.length != 3) {
      return;
    }

    return !age ||
      age > allowedUpper ||
      age < allowedLower ||
      dobYear.length !== 4 // chrome allows 2 char years
      ? "Please enter a valid date as DD-MM-YYYY"
      : "";
  };

  const validateSSNField = (field, ssn) => {
    if (!ssn) return "";
    return /^(?!(000|666|9))\d{3}-(?!00)\d{2}-(?!0000)\d{4}$/.test(ssn)
      ? ""
      : "Please enter a valid Social Security Number";
  };

  const validateBSBField = (field, bsb) => {
    if (!bsb) return "";
    return bsb.length === 7 && /^[0-9]{3}-[0-9]{3}$/.test(bsb)
      ? ""
      : "Please enter a valid BSB number";
  };

  const validateAccountNumberField = (field, accountNo) => {
    if (!accountNo) return "";
    return accountNo.length <= 9 &&
      accountNo.length >= 5 &&
      /^[0-9]+$/.test(accountNo)
      ? ""
      : "Please enter a valid account number";
  };

  const validateIBANField = (field, iban) => {
    if (!iban) return "";
    return iban.length <= 34 && iban.length >= 5 && /^[A-Za-z0-9 ]*$/.test(iban)
      ? ""
      : "Please enter a valid IBAN number";
  };

  const validateWalletAddressTag = (walletTag, walletTagData) => {
    if (!walletTag || !walletTagData) {
      return "";
    }

    const { name, regex } = walletTagData;
    const coinRegex = new RegExp(regex);
    return `${walletTag}`.search(coinRegex) === -1
      ? `Enter a valid wallet ${name.toLowerCase()}`
      : "";
  };

  const validateCurpField = (field, curp) => {
    if (!curp) {
      return "";
    }
    return /^[a-zA-Z]{4}\d{6}[a-zA-Z]{6}[a-zA-Z0-9]{2}$/.test(curp)
      ? ""
      : "Please enter a valid CURP number";
  };

  const validateCpfField = (field, cpf) => {
    if (!cpf) {
      return "";
    }
    return /^\d{11}$/.test(cpf) ? "" : "Please enter a valid CPF number";
  };

  const validateSaidField = (field, said) => {
    if (!said) {
      return "";
    }
    return /^\d{13}$/.test(said) ? "" : "Please enter a valid SAID number";
  };

  const validateSortCodeField = (field, sortCode) => {
    if (!sortCode) return "";
    return sortCode.length === 8 &&
      /^[0-9]{2}-[0-9]{2}-[0-9]{2}$/.test(sortCode)
      ? ""
      : "Please enter a valid sort code";
  };

  const validateCcField = (field, cc) => {
    if (!cc) {
      return "";
    }
    return /^\d{6,10}$/.test(cc) ? "" : "Please enter a valid CC number";
  };

  const validateDniField = (field, dni) => {
    if (!dni) {
      return "";
    }
    return /^\d{7,9}$|^\d{11}$/.test(dni)
      ? ""
      : "Please enter a valid DNI number";
  };

  const validateCiField = (field, ci) => {
    if (!ci) {
      return "";
    }
    return /^\d{7,8}([\dkK]){1}$/.test(ci)
      ? ""
      : "Please enter a valid CI number";
  };

  const validateTicField = (field, tic) => {
    if (!tic) {
      return "";
    }
    return /^\d{13}$/.test(tic) ? "" : "Please enter a valid TIC number";
  };

  return {
    validateNameField,
    validateEmailField,
    validateCountryField,
    validateCheckboxField,
    validateVerificationCodeField,
    validateWalletAddressField,
    validateBlockchainField,
    validateFileInput,
    validateMultiFileInput,
    validateMobileField,
    validateFiatField,
    validateCoinField,
    validateDobField,
    validateCurpField,
    validateCpfField,
    validateSaidField,
    validateSSNField,
    validateOccupationField,
    validateBSBField,
    validateAccountNumberField,
    validateIBANField,
    validateWalletAddressTag,
    validateSortCodeField,
    validateCcField,
    validateDniField,
    validateCiField,
    validateTicField,
  };
}
